import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useI18n } from '@/shared/i18n/useT';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  title: string;
  description?: string | undefined;
  children?: ReactNode;
  /** Buttons, rendered in a row at the bottom. */
  footer?: ReactNode;
  error?: string | null;
  onClose: () => void;
}

/**
 * The modal shell: overlay, Escape, focus trap, focus restore, and a bottom
 * sheet on a phone that becomes a centred card from `sm` up.
 *
 * Extracted from `ShareDialog`, which had all of this inline. Folders needs three
 * more dialogs and a fourth copy of a focus trap is how one of them ends up
 * without it.
 */
export function Dialog({ title, description, children, footer, error, onClose }: Props) {
  const { t } = useI18n();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((element) => element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Whatever opened the dialog gets the focus back when it closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => opener?.focus();
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--overlay)] p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className={[
          'flex max-h-[92dvh] w-full max-w-lg flex-col overflow-y-auto',
          'rounded-t-token-lg bg-surface p-4 text-fg sm:rounded-token-lg sm:p-6',
          'pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-6',
          'border border-border shadow-[0_4px_16px_rgb(15_23_42_/_0.08)]',
        ].join(' ')}
      >
        <header className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold sm:text-lg">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="tap-target -mr-2 -mt-2 flex items-center justify-center rounded-full text-muted hover:text-fg"
          >
            <Icon name="close" />
          </button>
        </header>

        {description && <p className="mt-1 text-sm text-muted">{description}</p>}

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-full border border-danger px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        {children && <div className="mt-4 min-h-0">{children}</div>}

        {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
